"use strict";
/**
 * Element Popup service — fetches a hydrated popup payload for a
 * single element + use case combination from the AI Hub backend.
 *
 * The backend response is shaped by `popup-service.ts` in /backend
 * and groups the canonical element row, the current-use-case
 * mention, and the cross-portfolio rollup into one payload.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.restoreElement = exports.softDeleteElement = exports.reviewElementMention = exports.fetchElementPopup = void 0;
var config_1 = require("../../../../config");
/**
 * GET /api/ai/elements/:elementId/popup?useCaseId=...
 *
 * Returns null when the element is not (yet) in the canonical store.
 * Callers should fall back to mock content in that case so the UI
 * stays usable during the gradual rollout described in plan
 * section 6 (Week 1 vs. Week 4).
 */
exports.fetchElementPopup = function (elementId, useCaseId) { return __awaiter(void 0, void 0, void 0, function () {
    var apiGet, query, url, result, error_1;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                apiGet = config_1.getApiGet();
                query = useCaseId ? "?useCaseId=" + encodeURIComponent(useCaseId) : '';
                url = "/ai/elements/" + encodeURIComponent(elementId) + "/popup" + query;
                _a.label = 1;
            case 1:
                _a.trys.push([1, 3, , 4]);
                return [4 /*yield*/, apiGet(url)];
            case 2:
                result = _a.sent();
                // Defensive: some apiGet implementations resolve with `{}` for
                // non-2xx responses instead of throwing. Treat anything missing
                // the canonical `element` payload as a miss so the popup falls
                // back to mock content rather than crashing the adapter.
                if (!result || typeof result !== 'object' || !result.element) {
                    return [2 /*return*/, null];
                }
                return [2 /*return*/, result];
            case 3:
                error_1 = _a.sent();
                // 404s and transport errors are non-fatal — the popup falls back
                // to mock data so the user still sees content. We surface a
                // single console warning to aid debugging without leaking
                // backend error text to the UI.
                console.warn('[element-popup] fetch failed:', error_1.message);
                return [2 /*return*/, null];
            case 4: return [2 /*return*/];
        }
    });
}); };
/**
 * Confirm or reject the current mention so the canonical store
 * learns from the manager's review. Returns the updated decision
 * payload or `null` on failure (UI swallows transport errors so
 * the popup stays usable).
 */
exports.reviewElementMention = function (mentionId, decision) { return __awaiter(void 0, void 0, void 0, function () {
    var url, apiPatch, result, error_2;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                url = "/ai/elements/mentions/" + mentionId + "/review";
                _a.label = 1;
            case 1:
                _a.trys.push([1, 3, , 4]);
                apiPatch = config_1.getApiPatch();
                return [4 /*yield*/, apiPatch(url, { decision: decision })];
            case 2:
                result = (_a.sent());
                return [2 /*return*/, result];
            case 3:
                error_2 = _a.sent();
                console.warn('[element-popup] review failed:', error_2.message);
                return [2 /*return*/, null];
            case 4: return [2 /*return*/];
        }
    });
}); };
/**
 * Soft-hide an element from active catalog surfaces.
 */
exports.softDeleteElement = function (elementId) { return __awaiter(void 0, void 0, void 0, function () {
    var apiPatch, error_3;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                apiPatch = config_1.getApiPatch();
                return [4 /*yield*/, apiPatch("/ai/elements/" + encodeURIComponent(elementId) + "/soft-delete", {})];
            case 1:
                _a.sent();
                return [2 /*return*/, true];
            case 2:
                error_3 = _a.sent();
                console.warn('[element-popup] soft-delete failed:', error_3.message);
                return [2 /*return*/, false];
            case 3: return [2 /*return*/];
        }
    });
}); };
/**
 * Restore a previously soft-hidden element back to active surfaces.
 */
exports.restoreElement = function (elementId) { return __awaiter(void 0, void 0, void 0, function () {
    var apiPatch, error_4;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                _a.trys.push([0, 2, , 3]);
                apiPatch = config_1.getApiPatch();
                return [4 /*yield*/, apiPatch("/ai/elements/" + encodeURIComponent(elementId) + "/restore", {})];
            case 1:
                _a.sent();
                return [2 /*return*/, true];
            case 2:
                error_4 = _a.sent();
                console.warn('[element-popup] restore failed:', error_4.message);
                return [2 /*return*/, false];
            case 3: return [2 /*return*/];
        }
    });
}); };
